import {
  FormGroup,
  FormSelect,
  FormSelectOption,
  TextInput,
  Radio,
  Checkbox,
  Grid,
  GridItem,
} from '@patternfly/react-core'
import * as React from 'react'

import type { Question, QuestionOption } from '@app/domain/instrument'
import { QuestionTypeId } from '@app/domain/instrument/instrument.interface'
import type { ValidationStatusId } from '@app/lib/validators'
import { theme } from '@app/utils/theme'

import {
  FormInputType,
  convertUnitRecordToString,
  parseFormattedUnitResponseString,
} from '../form.helpers'

import type {
  DatePickerProps,
  RadioProps,
  CheckboxProps,
} from '@patternfly/react-core'

export enum TextInputType {
  Text = 'text',
  Date = 'date',
  DateTimeLocal = 'datetime-local',
  Email = 'email',
  Month = 'month',
  Number = 'number',
  Password = 'password',
  Search = 'search',
  Tel = 'tel',
  Time = 'time',
  URL = 'url',
}

export type FormInput = IFormTextInputProps | IFormSelectInputProps

export interface IFormTextInputProps {
  inputType: FormInputType
  id: string
  name: string
  label: string
  value: string
  required?: boolean
  type?: TextInputType
  isReadOnly?: boolean
  isDisabled?: boolean
  validated?: ValidationStatusId
  helperTextInvalid?: string
  changeHandler?: (v: string) => void
  hideFormGroup?: boolean
}

export interface IFormSelectInputProps {
  inputType: FormInputType
  id: string
  label: string
  value: string
  options: Record<string, string>
  required?: boolean
  isDisabled?: boolean
  validated?: ValidationStatusId
  helperTextInvalid?: string
  changeHandler?: (v: string) => void
  hideFormGroup?: boolean
}

export interface IFormDateInputProps
  extends Pick<DatePickerProps, 'isDisabled' | 'value'> {
  changeHandler?: (v: string) => void
}

export interface IFormRadioInputProps
  extends Pick<RadioProps, 'isDisabled' | 'value' | 'id'> {
  changeHandler?: (v: string) => void
  options: QuestionOption[]
}

export interface IFormCheckboxInputProps
  extends Pick<CheckboxProps, 'isDisabled' | 'id'> {
  checkBoxChangeHandler?: (responseValues: string[], v: string) => void
  options: QuestionOption[]
  value: string
}

export const FormTextInput = ({
  changeHandler,
  helperTextInvalid,
  id,
  isDisabled,
  isReadOnly,
  label,
  name,
  required,
  type,
  validated,
  value,
  hideFormGroup,
}: IFormTextInputProps) => {
  if (hideFormGroup) {
    return (
      <TextInput
        id={id}
        name={name}
        value={value}
        isRequired={required ?? false}
        type={type ?? TextInputType.Text}
        onChange={changeHandler ? changeHandler : null}
        isReadOnly={isReadOnly}
        isDisabled={isDisabled}
        aria-label={value}
      />
    )
  }

  return (
    <FormGroup
      key={id}
      label={label}
      isRequired={required}
      fieldId={id}
      validated={validated}
      helperTextInvalid={helperTextInvalid}
    >
      <TextInput
        id={id}
        name={name}
        value={value}
        isRequired={required ?? false}
        type={type ?? TextInputType.Text}
        onChange={changeHandler ? changeHandler : null}
        isReadOnly={isReadOnly}
        isDisabled={isDisabled}
        aria-label={value}
      />
    </FormGroup>
  )
}

export const FormSelectInput = ({
  id,
  label,
  value,
  options,
  required,
  isDisabled,
  validated,
  helperTextInvalid,
  changeHandler,
  hideFormGroup,
}: IFormSelectInputProps) => {
  if (hideFormGroup) {
    return (
      <FormSelect
        value={value}
        isRequired={required}
        isDisabled={isDisabled}
        onChange={changeHandler}
      >
        {Object.keys(options).map((key, idx) => (
          <FormSelectOption key={idx} value={key} label={options[key]} />
        ))}
      </FormSelect>
    )
  }

  return (
    <FormGroup
      id={id}
      key={id}
      label={label}
      isRequired={required}
      validated={validated}
      helperTextInvalid={helperTextInvalid}
      fieldId={id}
    >
      <FormSelect
        value={value}
        isRequired={required}
        isDisabled={isDisabled}
        onChange={changeHandler}
      >
        {Object.keys(options).map((key, idx) => (
          <FormSelectOption key={idx} value={key} label={options[key]} />
        ))}
      </FormSelect>
    </FormGroup>
  )
}

export const FormDateInput = ({
  value,
  isDisabled,
  changeHandler,
}: IFormDateInputProps) => {
  return (
    <TextInput
      type="date"
      isDisabled={isDisabled}
      value={value}
      onChange={changeHandler}
    />
  )
}

export const FormRadioInput = ({
  changeHandler,
  id,
  isDisabled,
  options,
  value,
}: IFormRadioInputProps) => {
  return (
    <>
      {options.map((option, index) => (
        <Radio
          name={id}
          key={`${id}-${index}`}
          checked={value === option.value}
          id={`${id}-${index}`}
          label={
            <span
              dangerouslySetInnerHTML={{
                __html: option.label,
              }}
            />
          }
          isDisabled={isDisabled}
          value={option.label}
          onChange={() => changeHandler(option.value)}
        />
      ))}
    </>
  )
}

export const FormCheckboxInput = ({
  checkBoxChangeHandler,
  id,
  isDisabled,
  options,
  value,
}: IFormCheckboxInputProps) => {
  let responseValues: string[] = []
  if (value) {
    responseValues = value.split('|')
  }

  return (
    <>
      {options.map((option, index) => (
        <Checkbox
          name={id}
          key={index}
          isChecked={responseValues.findIndex((v) => v === option.value) !== -1}
          id={id}
          label={
            <span
              dangerouslySetInnerHTML={{
                __html: option.label,
              }}
            />
          }
          isDisabled={isDisabled}
          value={option.value}
          onChange={() => checkBoxChangeHandler(responseValues, option.value)}
        />
      ))}
    </>
  )
}

export interface IFormGroupInputProps {
  handleCheckboxResponseChange?: (
    questionId: string,
    responseValues: string[],
    v: string,
  ) => void
  isDisabled: boolean
  helperTextInvalid?: string
  onOptionChanged?: (questionId: string, v: string) => void
  question: Question
  responses: Record<string, string>
  validated?: ValidationStatusId
  showDisableReason?: boolean
  disabledReason?: string
}

export const FormInputGroup = ({
  handleCheckboxResponseChange,
  isDisabled,
  onOptionChanged,
  question,
  responses,
  showDisableReason,
  disabledReason,
}: IFormGroupInputProps) => {
  switch (question.inputType) {
    case QuestionTypeId.Text: {
      return (
        <>
          {disabledReason && showDisableReason && isDisabled ? (
            <small style={{ color: theme.colors.gray }}>{disabledReason}</small>
          ) : null}
          <FormTextInput
            label={question.label}
            isDisabled={isDisabled}
            required={question.required}
            id={question.id}
            name={question.id}
            value={responses[question.id]}
            type={TextInputType.Text}
            changeHandler={(value: string) =>
              onOptionChanged(question.id, value)
            }
            inputType={FormInputType.Text}
            hideFormGroup={true}
          />
        </>
      )
    }
    case QuestionTypeId.NumericWithUnits: {
      const parsedNumericUnitsResponse = parseFormattedUnitResponseString(
        responses[question.id],
      )
      return (
        <>
          {disabledReason && showDisableReason && isDisabled ? (
            <small style={{ color: theme.colors.gray }}>{disabledReason}</small>
          ) : null}
          <Grid hasGutter>
            <GridItem span={8}>
              <FormTextInput
                label={question.label}
                isDisabled={isDisabled}
                required={question.required}
                id={question.id}
                name={question.id}
                value={parsedNumericUnitsResponse?.value}
                type={TextInputType.Text}
                changeHandler={(v: string) =>
                  onOptionChanged(
                    question.id,
                    convertUnitRecordToString({
                      value: v,
                      unit:
                        parsedNumericUnitsResponse?.unit ??
                        question.options[0]?.value,
                    }),
                  )
                }
                inputType={FormInputType.Text}
                hideFormGroup={true}
              />
            </GridItem>
            <GridItem span={4}>
              <FormSelect
                id="unit"
                isRequired
                value={parsedNumericUnitsResponse?.unit}
                onChange={(v: string) =>
                  onOptionChanged(
                    question.id,
                    convertUnitRecordToString({
                      value: parsedNumericUnitsResponse?.value,
                      unit: v,
                    }),
                  )
                }
              >
                {question.options.map((option) => (
                  <FormSelectOption
                    id={`${question.id}_${option.value}`}
                    key={`${question.id}_${option.value}`}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </FormSelect>
            </GridItem>
          </Grid>
        </>
      )
    }
    case QuestionTypeId.Date: {
      return (
        <>
          {disabledReason && showDisableReason && isDisabled ? (
            <>
              <small style={{ color: theme.colors.gray }}>
                {disabledReason}
              </small>{' '}
              <br />
            </>
          ) : null}
          <FormDateInput
            isDisabled={isDisabled}
            value={responses[question.id]}
            changeHandler={(value: string) =>
              onOptionChanged(question.id, value)
            }
          />
        </>
      )
    }
    case QuestionTypeId.Radio: {
      return (
        <>
          {disabledReason && showDisableReason && isDisabled ? (
            <small style={{ color: theme.colors.gray }}>{disabledReason}</small>
          ) : null}
          <FormRadioInput
            id={question.id}
            options={question.options}
            isDisabled={isDisabled}
            value={responses[question.id]}
            changeHandler={(value: string) =>
              onOptionChanged(question.id, value)
            }
          />
        </>
      )
    }
    case QuestionTypeId.Checkbox: {
      return (
        <>
          {disabledReason && showDisableReason && isDisabled ? (
            <small style={{ color: theme.colors.gray }}>{disabledReason}</small>
          ) : null}
          <FormCheckboxInput
            options={question.options}
            isDisabled={isDisabled}
            id={question.id}
            value={responses[question.id]}
            checkBoxChangeHandler={(responseValues: string[], value: string) =>
              handleCheckboxResponseChange(question.id, responseValues, value)
            }
          />
        </>
      )
    }
    case QuestionTypeId.Binary: {
      return (
        <>
          {disabledReason && showDisableReason && isDisabled ? (
            <small style={{ color: theme.colors.gray }}>{disabledReason}</small>
          ) : null}
          <FormRadioInput
            id={question.id}
            options={question.options}
            isDisabled={isDisabled}
            value={responses[question.id]}
            changeHandler={(value: string) =>
              onOptionChanged(question.id, value)
            }
          />
        </>
      )
    }
    case QuestionTypeId.Scale:
    case QuestionTypeId.VisualAnalogScale: {
      return (
        <>
          {disabledReason && showDisableReason && isDisabled ? (
            <small style={{ color: theme.colors.gray }}>{disabledReason}</small>
          ) : null}
          <FormTextInput
            label={question.label}
            isDisabled={isDisabled}
            required={question.required}
            id={question.id}
            name={question.id}
            value={responses[question.id]}
            type={TextInputType.Number}
            changeHandler={(value: string) =>
              onOptionChanged(question.id, value)
            }
            inputType={FormInputType.Text}
            hideFormGroup={true}
          />
        </>
      )
    }
    default: {
      return <span>Unrecognized input</span>
    }
  }
}
